import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  classroomContextId,
  withClassroomContext,
} from "@/components/workspace/navigation";
import api from "@/utils/axios-utils";
import SpaceWelcome from "./SpaceWelcome";
import { useSpaceStore } from "../store/useSpaceStore";
import {
  LiveSpaceCard,
  ClosedSpaceCard,
  type SpaceSummary,
} from "./SpaceCards";
import { SpaceCardListSkeleton } from "./SpaceLoadingSkeletons";
import { useSpacesEventStream } from "../hooks/spacesEventStreamHook";
import type {
  SpaceCardUpdatedPayload,
  SpacePresenceChangedPayload,
} from "../hooks/spacesSSEEventPayloadTypes";

type AvailableSpace = SpaceSummary & {
  teacherName: string | null;
};

export default function StudentSpaceList() {
  const navigate = useNavigate();
  const location = useLocation();
  const classroomId = classroomContextId(location.pathname, location.search);
  const [spaces, setSpaces] = useState<AvailableSpace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<AvailableSpace[]>("/spaces/available")
      .then((res) => setSpaces(res.data))
      .catch(() => setError("Failed to load spaces. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  function handleEnter(space: SpaceSummary) {
    useSpaceStore
      .getState()
      .setSpace(space.spaceId, space.spaceName, space.lessonContext);
    navigate(withClassroomContext(`/spaces/${space.spaceId}`, classroomId));
  }

  const handleSpaceCardUpdated = useCallback(
    (payload: SpaceCardUpdatedPayload) => {
      setSpaces((prev) => {
        if (prev.some((s) => s.spaceId === payload.spaceId)) {
          return prev.map((s) =>
            s.spaceId === payload.spaceId
              ? {
                  ...s,
                  ...payload,
                  teacherName: payload.teacherName ?? s.teacherName,
                  status: payload.status as AvailableSpace["status"],
                }
              : s,
          );
        }
        return [
          ...prev,
          {
            ...payload,
            status: payload.status as AvailableSpace["status"],
            teacherName: payload.teacherName ?? null,
            lessonContext: null,
          },
        ];
      });
    },
    [],
  );

  const handleSpacePresenceChanged = useCallback(
    ({
      spaceId,
      connectedCount,
      participants,
    }: SpacePresenceChangedPayload) => {
      setSpaces((prev) =>
        prev.map((s) =>
          s.spaceId === spaceId
            ? { ...s, connectedCount, connectedParticipants: participants }
            : s,
        ),
      );
    },
    [],
  );

  useSpacesEventStream(!loading && !error, {
    onSpaceCardUpdated: handleSpaceCardUpdated,
    onSpacePresenceChanged: handleSpacePresenceChanged,
  });

  const scopedSpaces = classroomId
    ? spaces.filter((s) => s.classroomIds.includes(Number(classroomId)))
    : spaces;
  const openSpaces = scopedSpaces.filter(
    (s) => s.status === "open" || s.status === "paused",
  );
  const closedSpaces = scopedSpaces.filter((s) => s.status === "closed");

  const byClassroom = closedSpaces.reduce<
    Record<string, { name: string; spaces: AvailableSpace[] }>
  >((acc, space) => {
    const index = classroomId
      ? space.classroomIds.indexOf(Number(classroomId))
      : 0;
    const key = String(space.classroomIds[index] ?? "unknown");
    const name = space.classroomNames[index] ?? "Other";
    if (!acc[key]) acc[key] = { name, spaces: [] };
    acc[key].spaces.push(space);
    return acc;
  }, {});

  const lastOpenSpace = openSpaces[0] ?? null;

  return (
    <div className="flex flex-col gap-8">
      <SpaceWelcome
        variant="student"
        lastActiveRoom={lastOpenSpace}
        classroomId={classroomId}
      />

      {loading && <SpaceCardListSkeleton />}

      {error && <p className="text-sm text-red-500 font-mulish">{error}</p>}

      {!loading && !error && scopedSpaces.length === 0 && (
        <p className="text-gray-500 text-sm font-mulish text-center">
          No spaces in your classrooms yet.
        </p>
      )}

      {!loading && openSpaces.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl font-mulish font-bold text-darkgreen">
            Live now
          </h2>
          {openSpaces.map((space) => (
            <LiveSpaceCard
              key={space.spaceId}
              space={space}
              onEnter={handleEnter}
            />
          ))}
        </div>
      )}

      {!loading && closedSpaces.length > 0 && (
        <div className="flex flex-col gap-5">
          <h2 className="text-2xl font-mulish font-bold text-gray-400">
            Past Spaces
          </h2>
          {Object.values(byClassroom).map(
            ({ name, spaces: classroomSpaces }) => (
              <div key={name} className="flex flex-col gap-2">
                <span className="text-xs font-semibold font-mulish text-gray-400 uppercase tracking-wider px-1">
                  {name}
                </span>
                {classroomSpaces.map((space) => (
                  <ClosedSpaceCard
                    key={space.spaceId}
                    space={space}
                    onViewRecap={(spaceId) =>
                      navigate(
                        withClassroomContext(
                          `/spaces/${spaceId}/student-recap`,
                          classroomId,
                        ),
                      )
                    }
                    viewRecapLabel="View Recap"
                  />
                ))}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
